import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Close } from '@mui/icons-material';

import { useStore, useToast } from 'hooks';
import api from 'services/API';
import Dialog from 'components/shared/dialogs/Dialog';
import clsx from 'clsx';
import { dateUtilities } from 'utils';
import { useTranslation } from 'react-i18next';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ManageDistributorModal = ({
  open,
  onClose,
  distributor = null,
  onCreateSuccess,
  onDeleteSuccess,
}) => {
  const { t } = useTranslation();
  const { establishmentStore, distributorsStore, ui } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const { successToast } = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      name: distributor?.name || '',
      delivery_schedules: [{ deliveryDay: '', orderDay: '' }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'delivery_schedules',
  });

  const newSchedules = () => {
    return getValues().delivery_schedules.filter(schedule => {
      if (!schedule.scheduleId) return true;
      return false;
    });
  };

  const updatedSchedules = () => {
    return getValues().delivery_schedules.filter(schedule => {
      if (!schedule.scheduleId) return false;

      const existing = distributorsStore
        .getDistributorSchedulesByDistributorId(distributor.id)
        .find(s => s.id === schedule.scheduleId);

      if (!existing) return false;
      const hasChanged =
        existing.order_day_of_week !== schedule.orderDay ||
        existing.delivery_frequency_days !==
          dateUtilities.getDeliveryFrequencyDays(schedule.orderDay, schedule.deliveryDay);
      return hasChanged;
    });
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const newSchedulesList = newSchedules();
      const updatedSchedulesList = updatedSchedules();
      if (distributor) {
        await handleNewSchedules(newSchedulesList);
        await handleUpdatedSchedules(updatedSchedulesList);

        if (distributor.name !== getValues().name) {
          await updateDistributorName();
        }
        if (onCreateSuccess) onCreateSuccess(distributor);
      } else {
        const response = await createDistributorAndSchedules();
        if (onCreateSuccess) onCreateSuccess(response);
      }
      successToast(t('config.messages.common.success'));
      resetForm();
      ui.setDistributorModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewSchedules = async newSchedulesList => {
    if (newSchedulesList.length) {
      const payload = newSchedulesList.map(schedule => ({
        distributor_id: distributor.id,
        order_day_of_week: schedule.orderDay,
        delivery_frequency_days: dateUtilities.getDeliveryFrequencyDays(
          schedule.orderDay,
          schedule.deliveryDay,
        ),
      }));

      await Promise.all(payload.map(schedule => distributorsStore.createSchedules(schedule)));
    }
  };

  const handleUpdatedSchedules = async updatedSchedulesList => {
    if (updatedSchedulesList.length) {
      const payload = updatedSchedulesList.map(schedule => ({
        id: schedule.scheduleId,
        order_day_of_week: schedule.orderDay,
        delivery_frequency_days: dateUtilities.getDeliveryFrequencyDays(
          schedule.orderDay,
          schedule.deliveryDay,
        ),
      }));

      await Promise.all(
        payload.map(schedule => distributorsStore.updateSchedules(schedule.id, schedule)),
      );
    }
  };

  const updateDistributorName = async () => {
    if (!getValues().name) return;
    const distributorsPayload = { name: getValues().name };
    const distributorsResponse = await api.patchDistributor(distributor?.id, distributorsPayload);
    distributorsStore.patchDistributor(distributorsResponse?.data?.row);
  };

  const createDistributorAndSchedules = async () => {
    try {
      const distributorsPayload = {
        name: getValues().name,
        establishment_id: establishmentStore.establishment.id,
      };
      const distributor = await distributorsStore.createDistributor(distributorsPayload);
      if (!distributor.id) {
        console.error(t('Inventory.noDistributorIDReturned'));
        setIsLoading(false);
        return;
      }

      const payload = getValues().delivery_schedules.map(schedule => ({
        distributor_id: distributor.id,
        order_day_of_week: schedule.orderDay,
        delivery_frequency_days: dateUtilities.getDeliveryFrequencyDays(
          schedule.orderDay,
          schedule.deliveryDay,
        ),
      }));
      if (payload?.length) {
        for (const schedule of payload) {
          await distributorsStore.createSchedules(schedule); // Await each schedule creation for save creation order
        }
      }
      return distributor;
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    reset({
      name: '',
      delivery_schedules: [{ deliveryDay: '', orderDay: '' }],
    });
  };

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const onScheduleRemove = async (id, index) => {
    try {
      if (!id) {
        remove(index);
        return;
      }
      setIsLoading(true);
      await distributorsStore.deleteSchedule(id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (distributor) {
      const schedules = distributorsStore.getDistributorSchedulesByDistributorId(distributor.id);

      if (schedules?.length) {
        const delivery_schedules = schedules.map(schedule => ({
          orderDay: schedule.order_day_of_week,
          deliveryDay: dateUtilities.getDeliveryDayByFrequency(
            schedule.order_day_of_week,
            schedule.delivery_frequency_days,
          ),
          scheduleId: schedule.id,
        }));

        reset({
          name: getValues().name !== '' ? getValues().name : distributor?.name,
          delivery_schedules,
        });
      } else {
        reset({
          name: getValues().name !== '' ? getValues().name : distributor?.name,
          delivery_schedules: [{ deliveryDay: '', orderDay: '' }],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributor, distributorsStore.distributorsSchedules.length]);

  return (
    <StyledDialog
      open={open}
      onClose={() => closeModal(true)}
      fullWidth
      title={distributor ? t('Inventory.editDistributor') : t('Inventory.addDistributor')}
    >
      <Box className="distributor_wrapper">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className="dialogContent">
            <Typography variant="h5" className="label">
              {t('Inventory.nameOfDistributor')}
            </Typography>

            <FormControl variant="outlined" fullWidth className="control" error={!!errors.name}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: t('Inventory.requiredDistributor'),
                  pattern: {
                    value: /^(?=.*[a-zA-Z0-9]).+$/,
                    message: t('Inventory.requiredDistributorDescription'),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Inventory.nameOfDistributor')}
                    variant="outlined"
                    fullWidth
                    className="input"
                  />
                )}
              />
              <FormHelperText>{errors.name?.message}</FormHelperText>
            </FormControl>

            <Divider className="divider" />

            <Typography variant="h5">{t('Inventory.deliverySchedule')}</Typography>
            <Typography variant="body2" className="subtitle">
              {t('Inventory.deliveryScheduleDescription')}
            </Typography>

            {fields.map((field, index) => (
              <Box
                key={field.id}
                className="delivery"
                data-testid={`delivery-${index}`}
                sx={{ position: 'relative', paddingRight: fields.length > 1 && '32px' }}
              >
                {fields.length > 1 && (
                  <IconButton
                    aria-label="Close"
                    disabled={isLoading}
                    onClick={() => onScheduleRemove(field.scheduleId, index)}
                    size="small"
                    sx={{ position: 'absolute', top: 12, right: 0 }}
                  >
                    <Close />
                  </IconButton>
                )}

                <FormControl
                  variant="outlined"
                  fullWidth
                  className="control"
                  error={!!errors?.delivery_schedules?.[index]?.deliveryDay}
                >
                  <InputLabel id={`delivery-label-${index}`}>
                    {t('Inventory.deliveryDay')}
                  </InputLabel>
                  <Controller
                    name={`delivery_schedules.${index}.deliveryDay`}
                    control={control}
                    rules={{ required: t('Inventory.requiredDeliveryDay') }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId={`delivery-label-${index}`}
                        className="select"
                        label={t('Inventory.deliveryDay')}
                      >
                        {days.map(value => (
                          <MenuItem key={value} value={value}>
                            <span className="option">{t(`Inventory.${value}`)}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors?.delivery_schedules?.[index]?.deliveryDay?.message}
                  </FormHelperText>
                </FormControl>

                <FormControl
                  variant="outlined"
                  fullWidth
                  className="control"
                  error={!!errors?.delivery_schedules?.[index]?.orderDay}
                >
                  <InputLabel id={`order-label-${index}`}>{t('Inventory.orderDay')}</InputLabel>
                  <Controller
                    name={`delivery_schedules.${index}.orderDay`}
                    control={control}
                    rules={{ required: t('Inventory.requiredOrderDay') }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId={`order-label-${index}`}
                        className="select"
                        label={t('Inventory.orderDay')}
                      >
                        {days.map(value => (
                          <MenuItem key={t(value)} value={value}>
                            <span className="option">{t(`Inventory.${value}`)}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors?.delivery_schedules?.[index]?.orderDay?.message}
                  </FormHelperText>
                </FormControl>
              </Box>
            ))}

            <Button
              variant="text"
              className="footerBtn bordered addBtn"
              sx={{ textTransform: 'none' }}
              onClick={() => append({ deliveryDay: '', orderDay: '' })}
              type="button"
              disabled={isLoading}
            >
              {t('Inventory.addAnother')}
            </Button>
          </DialogContent>

          <DialogActions className="footer">
            <Button
              variant="text"
              className={clsx('footerBtn bordered', { deleteBorder: distributor })}
              disabled={isLoading}
              onClick={closeModal}
            >
              {t('Inventory.cancel')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              className="footerBtn"
              disabled={isLoading}
              type="submit"
            >
              {t('Inventory.saveAndClose')}
            </Button>
          </DialogActions>
        </form>
      </Box>
    </StyledDialog>
  );
};

export default observer(ManageDistributorModal);

const StyledDialog = styled(Dialog)(({ theme: { spacing, palette } }) => ({
  width: '460px',
  maxHeight: '650px',
  borderRadius: '16px',
  '.dialog_content': {
    padding: '28px',
    height: '100%',
    overflowY: 'scroll',
  },
  '.titleDark': {
    background: palette.secondary.main,
  },
  '.footer': {
    borderTop: '1px solid #313131',
    padding: '24px',
  },
  '.footerBtn': {
    textTransform: 'none',
    width: '100%',
    '&.bordered': {
      border: '1px solid #313131',
    },
    '&.addBtn': {
      background: 'rgba(40, 40, 40, 1)',
      border: 'none',
      marginBottom: '8px',
    },
    '&.deleteBorder': {
      border: '1px solid rgba(255, 122, 122, 1)',
    },
  },
  '.label': {
    marginBottom: '16px',
  },
  '.control': {
    marginBottom: '24px',
  },
  '.divider': {
    borderColor: '#313131',
    margin: '0 0 24px',
  },
  '.subtitle': {
    color: 'rgba(161, 161, 161, 1)',
    marginTop: '4px',
    marginBottom: '16px',
  },
  '.delivery': {
    display: 'flex',
    gap: '24px',
  },
  '.distributor_wrapper': {
    overflowY: 'scroll',
  },
}));
